import React from 'react';
import {
    Box,
    Flex,
    Heading,
    Select,
    Button,
    FormControl,
    FormLabel,
    useColorModeValue,
    useBreakpointValue,
} from '@chakra-ui/react';

interface FilterState {
    tahun_bulan: string;
    jenis_laporan: string;
    estate?: string;
}

interface FilterSectionProps {
    filters: FilterState;
    onFiltersChange: (filters: FilterState) => void;
    onApplyFilters: () => void;
    isLoading?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    onFiltersChange,
    onApplyFilters,
    isLoading = false
}) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    const headerPadding = useBreakpointValue({ 
        base: 2, 
        md: 2, 
        lg: 2,
        xl: 2,
        '2xl': 3
    });
    const headerMargin = useBreakpointValue({ 
        base: 2, 
        md: 2, 
        lg: 2,
        xl: 2
    });
    
    // Responsive values
    const isMobile = useBreakpointValue({ base: true, md: false });
    const headingSize = useBreakpointValue({ base: 'sm', md: 'md', lg: 'md' });
    const inputSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const labelFontSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const selectWidth = useBreakpointValue({ base: '180px', md: '200px', lg: '220px' });
    const buttonPadding = useBreakpointValue({ base: 2, md: 3 });

    // Get current year
    const currentYear = new Date().getFullYear();

    // Handle month selection change
    const handleMonthChange = (value: string) => {
        onFiltersChange({
            ...filters,
            tahun_bulan: value
        });
    };

    return (
        <Box bg="white" rounded="lg" shadow="md" p={headerPadding} mb={headerMargin} w="100%">
            {/* Desktop Layout */}
            {!isMobile ? (
                <Flex justify="space-between" align="center" w="100%" flexWrap="wrap" gap={2}>
                    <Heading
                        as="h1"
                        size={headingSize}
                        color="gray.800"
                        minW="fit-content"
                    >
                        DASHBOARD HARIAN PRODUKSI
                    </Heading>

                    {/* Month Filter */}
                    <Flex wrap="wrap" gap={2} align="center" minW="fit-content">
                        <FormControl w="auto" size={inputSize}>
                            <Flex align="center" gap={1} flexWrap="wrap">
                                <FormLabel
                                    htmlFor="month"
                                    fontSize={labelFontSize}
                                    color="gray.700"
                                    mb={0}
                                    whiteSpace="nowrap"
                                >
                                    Bulan:
                                </FormLabel>
                                <Select
                                    id="month"
                                    value={filters.tahun_bulan}
                                    onChange={(e) => handleMonthChange(e.target.value)}
                                    size={inputSize}
                                    borderRadius="md"
                                    focusBorderColor="blue.500"
                                    bg="white"
                                    w={selectWidth}
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = (i + 1).toString().padStart(2, '0');
                                        const value = `${currentYear}${month}`;
                                        return (
                                            <option key={month} value={value}>
                                                {new Date(currentYear, i).toLocaleString('id-ID', { month: 'long' })} {currentYear}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </Flex>
                        </FormControl>

                        <Button
                            onClick={onApplyFilters}
                            isLoading={isLoading}
                            loadingText="Memuat..."
                            colorScheme="blue"
                            size={buttonSize}
                            px={buttonPadding}
                            fontWeight="medium"
                        >
                            Refresh
                        </Button>
                    </Flex>
                </Flex>
            ) : (
                /* Mobile Layout */
                <Flex direction="column" gap={2} w="100%">
                    <Heading
                        as="h1"
                        size="sm"
                        color="gray.800"
                        textAlign="center"
                    >
                        DASHBOARD MILL
                    </Heading>

                    {/* Month Filter - Mobile/Tablet */}
                    <Flex justify="center" align="center" gap={2} flexWrap="wrap">
                        <FormControl w="auto" size="sm">
                            <Flex align="center" gap={2}>
                                <FormLabel
                                    htmlFor="month-mobile"
                                    fontSize="sm"
                                    color="gray.700"
                                    mb={0}
                                    whiteSpace="nowrap"
                                >
                                    Bulan:
                                </FormLabel>
                                <Select
                                    id="month-mobile"
                                    value={filters.tahun_bulan}
                                    onChange={(e) => handleMonthChange(e.target.value)}
                                    size="sm"
                                    borderRadius="md"
                                    focusBorderColor="blue.500"
                                    bg="white"
                                    w="180px"
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = (i + 1).toString().padStart(2, '0');
                                        const value = `${currentYear}${month}`;
                                        return (
                                            <option key={month} value={value}>
                                                {new Date(currentYear, i).toLocaleString('id-ID', { month: 'long' })} {currentYear}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </Flex>
                        </FormControl>

                        <Button
                            onClick={onApplyFilters}
                            isLoading={isLoading}
                            loadingText="Memuat..."
                            colorScheme="blue"
                            size="sm"
                            px={4}
                            fontWeight="medium"
                        >
                            Refresh
                        </Button>
                    </Flex>
                </Flex>
            )}
        </Box>
    );
};

export default FilterSection;